import AttachmentService from "../services/Attachment";
import axios from "axios";

const MAX_CONCURRENT_UPLOADS = 5;

export default class UploadManager {
  constructor() {
    this.progressListeners = [];
    this.successListeners = [];
    this.uploads = [];
  }

  addProgressListener = listener => {
    return this.progressListeners.push(listener) - 1;
  };

  addSuccessListener = listener => {
    return this.successListeners.push(listener) - 1;
  };

  removeProgressListener = id => {
    this.progressListeners.splice(id, 1);
  };

  removeSuccessListener = id => {
    this.successListeners.splice(id, 1);
  };

  dispatchProgressEvent = () => {
    this.progressListeners.forEach(listener => listener(this.uploads));
  };

  dispatchSuccessEvent = upload => {
    this.successListeners.forEach(listener => listener(upload));
  };

  updateUploadData = upload => {
    let index = this.getUploadIndex(upload.id);
    if (index === -1) {
      index = this.uploads.push({}) - 1;
    }
    this.uploads[index] = {
      ...this.uploads[index],
      ...upload,
    };
    return { ...this.uploads[index] };
  };

  getUploadId = file => {
    return `${file.name}-${file.lastModified}-${file.size}-${file.type}`;
  };

  getUploadIndex = id => {
    return this.uploads.findIndex(upload => upload.id === id);
  };

  isTracked = id => this.getUploadIndex(id) !== -1;

  uploadFiles = async files => {
    const uploads = files
      .map(file => ({
        file,
        id: this.getUploadId(file),
        status: "queued",
        loaded: 0,
        percent: 0,
        attachment: null,
        source: axios.CancelToken.source(),
      }))
      .filter(upload => !this.isTracked(upload.id));
    this.uploads = this.uploads.concat(uploads);
    this.dispatchProgressEvent();

    const uploadPromises = uploads.map(upload => this.startUpload(upload));

    const maxConcurrentUploads = Math.min(
      MAX_CONCURRENT_UPLOADS,
      uploads.length
    );
    const results = [];
    for (let i = 0; i < uploadPromises.length; i += maxConcurrentUploads) {
      const batch = uploadPromises.slice(i, i + maxConcurrentUploads);
      const batchResults = await Promise.allSettled(batch);
      results.push(...batchResults);
    }

    return results;
  };

  startUpload = async upload => {
    try {
      const attachment = await AttachmentService.uploadFile(
        upload.file,
        progress => {
          // A removed upload must not be re-inserted by a late progress event.
          if (!this.isTracked(upload.id)) return;
          this.updateUploadData({
            id: upload.id,
            status: "uploading",
            loaded: progress.loaded,
            percent: Math.round((progress.loaded / progress.total) * 100),
          });
          this.dispatchProgressEvent();
        },
        upload.source.token
      );
      const uploadData = this.updateUploadData({
        id: upload.id,
        status: "finished",
        attachment,
      });
      this.dispatchProgressEvent();
      this.dispatchSuccessEvent(uploadData);
      return uploadData;
    } catch (error) {
      // Cancellation comes from removeUpload, which already dropped the entry.
      if (this.isTracked(upload.id)) {
        this.updateUploadData({
          id: upload.id,
          status: "failed",
          error: error.message,
        });
        this.dispatchProgressEvent();
      }
      throw error;
    }
  };

  retryUpload = upload => {
    if (!this.isTracked(upload.id)) {
      return Promise.resolve();
    }
    const reset = this.updateUploadData({
      id: upload.id,
      status: "queued",
      loaded: 0,
      percent: 0,
      error: undefined,
      attachment: null,
      source: axios.CancelToken.source(),
    });
    this.dispatchProgressEvent();
    // The failure is already recorded on the entry and dispatched to
    // listeners, so a second rejection would only surface as unhandled.
    return this.startUpload(reset).catch(() => undefined);
  };

  removeUpload = upload => {
    const index = this.getUploadIndex(upload.id);
    if (index === -1) {
      return;
    }
    this.uploads.splice(index, 1);
    this.dispatchProgressEvent();
    upload.source.cancel();
  };

  removeUploads = () => {
    // Copied first: removeUpload splices the array being walked.
    [...this.uploads].forEach(upload => this.removeUpload(upload));
  };
}
